import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext.js'
import { useHolidayReview, refreshHolidayReview, isSnoozed, snooze } from '../utils/holidayReview.js'
import './CheckinToast.css'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'
const AUTO_DISMISS_MS = 8000

/** The calm to-do line for one tenant's holiday summary (see utils/holidayReview.js). */
function holidayReviewText(entry) {
  const parts = []
  if (entry.pending > 0) {
    const what = entry.pending === 1 ? '1 neuer Eintrag' : `${entry.pending} neue Einträge`
    parts.push(entry.years?.length ? `${what} für ${entry.years.join(', ')}` : what)
  }
  if (entry.failing) {
    const since = entry.failingSince ? new Date(`${entry.failingSince}T12:00:00Z`).toLocaleDateString('de-DE') : ''
    parts.push(`Feiertage konnten seit ${since} nicht geladen werden — bitte manuell prüfen`)
  }
  if (entry.missingYear) parts.push(`Feiertage für ${entry.missingYear} noch nicht eingetragen`)
  return parts.join(' · ')
}

export default function CheckinToast() {
  const { account, admin, apiFetch, getToken } = useAuth()
  const [toasts, setToasts] = useState([])
  const adminRef = useRef(admin)
  useEffect(() => { adminRef.current = admin }, [admin])

  // Whether this user asked to hear about check-ins STARTING. Read from their account
  // rather than from this browser, so turning it on at one desk does not have to be
  // repeated at the next. A ref: the SSE handlers below are created once per connection
  // and would otherwise close over a stale value.
  const startedNotifyRef = useRef(false)
  useEffect(() => {
    if (!account) return

    const load = () => apiFetch('/api/admin/notification-prefs')
      .then(r => r.json())
      .then(d => { startedNotifyRef.current = !!d.startedNotify })
      .catch(() => { /* leave it off: silence is the safe default for a notification */ })

    load()

    // Re-read when the Einstellungen page saves. This component is mounted outside the
    // Router and so never remounts while the dashboard is open: without this, switching
    // the notice on took effect only after a full page reload, which looks exactly like
    // the notice not working.
    window.addEventListener('notification-prefs-saved', load)
    return () => window.removeEventListener('notification-prefs-saved', load)
  }, [account, apiFetch])
  const apiFetchRef = useRef(apiFetch)
  apiFetchRef.current = apiFetch

  // Holidays to check. A to-do, not a live event: no sound, shown regardless of the
  // notification toggle, and "Morgen erinnern" only hides it until tomorrow. Decided
  // 2026-10-27 that it must look different from the alerts above it.
  const holidayReview = useHolidayReview(apiFetch, !!admin && admin.role !== 'user')
  const [, setSnoozeTick] = useState(0)
  const holidayTodos = holidayReview.filter(e => !isSnoozed(e.tenantId))

  function playSound() {
    try {
      const AudioCtx = window.AudioContext || (window).webkitAudioContext
      const ctx = new AudioCtx()
      // Two-tone chime: low note then high note
      const notes = [523.25, 783.99] // C5, G5
      notes.forEach((freq, i) => {
        const osc  = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.type = 'sine'
        osc.frequency.value = freq
        const start = ctx.currentTime + i * 0.18
        gain.gain.setValueAtTime(0, start)
        gain.gain.linearRampToValueAtTime(0.25, start + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.4)
        osc.start(start)
        osc.stop(start + 0.4)
      })
    } catch {}
  }

  function dismiss(id) {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  useEffect(() => {
    if (!account) return

    let es = null
    let retryTimeout = null
    let active = true

    async function connect() {
      if (!active) return
      let token
      try {
        token = await getToken()
      } catch {
        if (active) retryTimeout = setTimeout(connect, 5000)
        return
      }
      if (!active) return

      es = new EventSource(
        `${BACKEND_URL}/api/admin/events?token=${token}`
      )

      es.addEventListener('new-checkin', (e) => {
        try {
          if (localStorage.getItem('dashboard-notify-enabled') === 'false') return
          const data = JSON.parse(e.data)
          const currentAdmin = adminRef.current
          if (currentAdmin && currentAdmin.role !== 'owner' && data.tenantId !== currentAdmin.tenant_id) return
          const id = Date.now()
          if (localStorage.getItem('dashboard-notify-sound') !== 'false') playSound()
          setToasts(prev => [...prev, { id, ...data }])
          // Pending toasts stay until acted on — approved toasts auto-dismiss
          if (data.status !== 'pending') setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
        } catch {}
      })

      // Somebody has just walked up to a terminal and begun a check-in — sent at the
      // language screen, so there is no name yet and none is claimed. Its only job is
      // "in a few seconds somebody will need something from you".
      //
      // Not the 'session-started' event the board refreshes on: that one arrives a page
      // or two later, once data has been entered. Opt-in per user (migration 040) and
      // checked FIRST: at a busy gate this fires for every visitor, including the ones
      // who turn round and leave again, so nobody gets it by default.
      es.addEventListener('checkin-started', (e) => {
        try {
          if (!startedNotifyRef.current) return
          if (localStorage.getItem('dashboard-notify-enabled') === 'false') return
          const data = JSON.parse(e.data)
          const currentAdmin = adminRef.current
          if (currentAdmin && currentAdmin.role !== 'owner' && data.tenantId !== currentAdmin.tenant_id) return

          const id = Date.now()
          if (localStorage.getItem('dashboard-notify-sound') !== 'false') playSound()
          setToasts(prev => [...prev, { id, kind: 'checkin-started', ...data }])
          // Auto-dismissed: this is "someone is there", not something to answer. Leaving
          // it up would stack one per visitor over the page staff are trying to work in.
          setTimeout(() => dismiss(id), AUTO_DISMISS_MS)
        } catch {}
      })

      // A visitor has reached a manual dynamic info page and is waiting for someone to
      // pick their page. Unlike a check-in notification this is time-critical — the
      // kiosk gives up after the step's timeout — and the assignment page is somewhere
      // the admin may not be looking, so it is surfaced here too.
      es.addEventListener('session-awaiting-assignment', (e) => {
        try {
          if (localStorage.getItem('dashboard-notify-enabled') === 'false') return
          const data = JSON.parse(e.data)
          const currentAdmin = adminRef.current
          if (currentAdmin && currentAdmin.role !== 'owner' && data.tenantId !== currentAdmin.tenant_id) return

          const id = Date.now()
          if (localStorage.getItem('dashboard-notify-sound') !== 'false') playSound()
          // Deliberately NOT auto-dismissed: someone is standing at a terminal.
          setToasts(prev => [...prev, { id, kind: 'awaiting-assignment', ...data }])
        } catch {}
      })

      // A ramp held past its time limit. Surfaced here and not only on the board,
      // because a colour change on a page nobody has open achieves nothing — and the
      // server deliberately sends this only once per assignment, so a dismissed toast
      // will not come back by itself.
      es.addEventListener('destination-overdue', (e) => {
        try {
          if (localStorage.getItem('dashboard-notify-enabled') === 'false') return
          const data = JSON.parse(e.data)
          const currentAdmin = adminRef.current
          if (currentAdmin && currentAdmin.role !== 'owner' && data.tenantId !== currentAdmin.tenant_id) return

          const id = Date.now()
          if (localStorage.getItem('dashboard-notify-sound') !== 'false') playSound()
          setToasts(prev => [...prev, { id, kind: 'destination-overdue', ...data }])
        } catch {}
      })

      // A ramp held by a check-in nobody finished. Only ever sent for a tenant that
      // treats that as an anomaly — where the gate is opened by hand after a call it
      // is the normal outcome, and alerting would fire for every visitor.
      es.addEventListener('destination-unfinished-checkin', (e) => {
        try {
          if (localStorage.getItem('dashboard-notify-enabled') === 'false') return
          const data = JSON.parse(e.data)
          const currentAdmin = adminRef.current
          if (currentAdmin && currentAdmin.role !== 'owner' && data.tenantId !== currentAdmin.tenant_id) return

          const id = Date.now()
          if (localStorage.getItem('dashboard-notify-sound') !== 'false') playSound()
          setToasts(prev => [...prev, { id, kind: 'destination-unfinished', ...data }])
        } catch {}
      })

      // A waiting area at or near capacity. Carries the free ramps, so the message
      // names what to do rather than only what is wrong.
      es.addEventListener('destination-capacity', (e) => {
        try {
          if (localStorage.getItem('dashboard-notify-enabled') === 'false') return
          const data = JSON.parse(e.data)
          const currentAdmin = adminRef.current
          if (currentAdmin && currentAdmin.role !== 'owner' && data.tenantId !== currentAdmin.tenant_id) return

          const id = Date.now()
          if (localStorage.getItem('dashboard-notify-sound') !== 'false') playSound()
          setToasts(prev => [...prev, { id, kind: 'destination-capacity', ...data }])
        } catch {}
      })

      // A barrier is still open — after its configured time, or at closing time. Only
      // sent for barriers configured for it in Stationen (off by default), once per
      // open period, so it is not dismissed automatically either.
      es.addEventListener('barrier-open-reminder', (e) => {
        try {
          if (localStorage.getItem('dashboard-notify-enabled') === 'false') return
          const data = JSON.parse(e.data)
          const currentAdmin = adminRef.current
          if (currentAdmin && currentAdmin.role !== 'owner' && data.tenantId !== currentAdmin.tenant_id) return

          const id = Date.now()
          if (localStorage.getItem('dashboard-notify-sound') !== 'false') playSound()
          setToasts(prev => [...prev, { id, kind: 'barrier-open-reminder', ...data }])
        } catch {}
      })

      // The timed auto-close could not reach the barrier — it may still be open.
      es.addEventListener('barrier-auto-close-failed', (e) => {
        try {
          const data = JSON.parse(e.data)
          const currentAdmin = adminRef.current
          if (currentAdmin && currentAdmin.role !== 'owner' && data.tenantId !== currentAdmin.tenant_id) return

          const id = Date.now()
          if (localStorage.getItem('dashboard-notify-sound') !== 'false') playSound()
          setToasts(prev => [...prev, { id, kind: 'barrier-auto-close-failed', ...data }])
        } catch {}
      })

      // The holiday job found something new for review. The event carries no details —
      // the shared list is fetched again, which the badge and the settings page use too.
      es.addEventListener('holidays-review', () => {
        refreshHolidayReview(apiFetchRef.current)
      })

      es.onerror = () => {
        es.close()
        if (active) retryTimeout = setTimeout(connect, 5000)
      }
    }

    connect()

    return () => {
      active = false
      clearTimeout(retryTimeout)
      es?.close()
    }
  }, [account, getToken])

  const handleApprove = async (toast) => {
    try {
      await apiFetch(`/api/admin/checkins/${toast.sessionId}/approve`, {
        method: 'POST',
        body:   JSON.stringify({ tenantId: toast.tenantId }),
      })
      dismiss(toast.id)
    } catch {}
  }

  const handleReject = async (toast) => {
    try {
      await apiFetch(`/api/admin/checkins/${toast.sessionId}/reject`, {
        method: 'POST',
        body:   JSON.stringify({ tenantId: toast.tenantId }),
      })
      dismiss(toast.id)
    } catch {}
  }

  if (toasts.length === 0 && holidayTodos.length === 0) return null

  return (
    <div className="ct-stack">
      {toasts.map(toast => {
        const isPending  = toast.status === 'pending'
        const isAwaiting = toast.kind === 'awaiting-assignment'

        // Someone has begun a check-in. Purely informational — no action and no name,
        // because at the language screen there is nothing yet to know and nothing to
        // decide. It says only that in a few seconds somebody will need something.
        // Deliberately the quietest toast here: no button, no pulse, grey.
        if (toast.kind === 'checkin-started') {
          return (
            <div key={toast.id} className="ct-toast ct-toast--started">
              <div className="ct-icon ct-icon--started">🚶</div>
              <div className="ct-body">
                <div className="ct-title ct-title--started">Check-in gestartet</div>
                <div className="ct-name">
                  {toast.stationName || 'Am Terminal'}
                </div>
                {admin?.role === 'owner' && toast.tenantSlug && (
                  <div className="ct-meta">{toast.tenantSlug}</div>
                )}
              </div>
              <button className="ct-close" onClick={() => dismiss(toast.id)}>✕</button>
            </div>
          )
        }

        // A visitor is standing at the terminal waiting for someone to pick their
        // page, and the kiosk gives up after the step's timeout — so this needs to
        // pull attention wherever the admin happens to be in the dashboard.
        if (isAwaiting) {
          return (
            <div key={toast.id} className="ct-toast ct-toast--awaiting">
              <div className="ct-icon ct-icon--awaiting">📍</div>
              <div className="ct-body">
                <div className="ct-title ct-title--awaiting">Zuweisung erforderlich</div>
                <div className="ct-name">{toast.displayName || 'Besucher'}</div>
                <div className="ct-meta">
                  wartet am Terminal
                  {admin?.role === 'owner' && toast.tenantSlug ? ` · ${toast.tenantSlug}` : ''}
                </div>
                <div className="ct-actions">
                  {/* A plain link, not react-router: CheckinToast is mounted outside
                      the Router, so useNavigate/Link are unavailable here. */}
                  <a className="ct-btn ct-btn--approve" href="/destination-board">
                    Jetzt zuweisen
                  </a>
                </div>
              </div>
              <button className="ct-close" onClick={() => dismiss(toast.id)}>✕</button>
            </div>
          )
        }

        if (toast.kind === 'barrier-open-reminder' || toast.kind === 'barrier-auto-close-failed') {
          const failed = toast.kind === 'barrier-auto-close-failed'
          const since = toast.openSince ? new Date(toast.openSince) : null
          const sinceText = since
            ? `${String(since.getHours()).padStart(2, '0')}:${String(since.getMinutes()).padStart(2, '0')}`
            : null
          // A failed close is red, not the orange of the reminders: the gate may be
          // standing open after hours, which is more urgent than "still open".
          const variant = failed ? 'failed' : 'awaiting'
          return (
            <div key={toast.id} className={`ct-toast ct-toast--${variant}`}>
              <div className={`ct-icon ct-icon--${variant}`}>🚧</div>
              <div className="ct-body">
                <div className={`ct-title ct-title--${variant}`}>
                  {failed ? 'Automatisches Schließen fehlgeschlagen' : 'Schranke noch offen'}
                </div>
                <div className="ct-name">{toast.label}</div>
                <div className="ct-meta">
                  {failed
                    ? `${toast.error || 'Schranke nicht erreichbar'} — bitte prüfen`
                    : toast.reminder === 'closing'
                      ? `Betriebsende ${toast.closingTime}${sinceText ? ` · offen seit ${sinceText}` : ''}`
                      : `seit ${toast.minutes} Min. offen${sinceText ? ` (seit ${sinceText})` : ''}`}
                </div>
                <div className="ct-actions">
                  <a className="ct-btn ct-btn--approve" href="/destination-board">
                    Öffnen
                  </a>
                </div>
              </div>
              <button className="ct-close" onClick={() => dismiss(toast.id)}>✕</button>
            </div>
          )
        }

        // A ramp is past its time limit. Three answers, exactly as on the board — the
        // point of alerting here is that it can be dealt with here, not that it sends
        // someone looking for another page.
        if (toast.kind === 'destination-overdue') {
          return (
            <div key={toast.id} className="ct-toast ct-toast--awaiting">
              <div className="ct-icon ct-icon--awaiting">⏱</div>
              <div className="ct-body">
                <div className="ct-title ct-title--awaiting">Zielort überfällig</div>
                <div className="ct-name">{toast.destination}</div>
                <div className="ct-meta">
                  {toast.visitor || toast.company || 'Unbekannt'} · {toast.overdueMinutes} Min. über der Zeit
                </div>
                <div className="ct-actions">
                  <a className="ct-btn ct-btn--approve" href="/destination-board">
                    Öffnen
                  </a>
                </div>
              </div>
              <button className="ct-close" onClick={() => dismiss(toast.id)}>✕</button>
            </div>
          )
        }

        // A ramp held by an unfinished check-in. Phrased as a question rather than a
        // statement: the system does not know whether the driver is standing there,
        // and the person reading this can find out.
        if (toast.kind === 'destination-unfinished') {
          return (
            <div key={toast.id} className="ct-toast ct-toast--awaiting">
              <div className="ct-icon ct-icon--awaiting">❓</div>
              <div className="ct-body">
                <div className="ct-title ct-title--awaiting">Check-in nicht abgeschlossen</div>
                <div className="ct-name">{toast.destination}</div>
                <div className="ct-meta">
                  {toast.visitor || toast.company || 'Unbekannt'} · seit {toast.minutes} Min. belegt,
                  kein Check-in
                </div>
                <div className="ct-actions">
                  <a className="ct-btn ct-btn--approve" href="/destination-board">
                    Prüfen
                  </a>
                </div>
              </div>
              <button className="ct-close" onClick={() => dismiss(toast.id)}>✕</button>
            </div>
          )
        }

        // A waiting area filling up. The free ramps are the actionable half, so they
        // are in the message rather than something to go and look up.
        if (toast.kind === 'destination-capacity') {
          return (
            <div key={toast.id} className="ct-toast ct-toast--awaiting">
              <div className="ct-icon ct-icon--awaiting">{toast.level === 'full' ? '🚫' : '⚠'}</div>
              <div className="ct-body">
                <div className="ct-title ct-title--awaiting">
                  {toast.level === 'full' ? 'Wartebereich voll' : 'Wartebereich füllt sich'}
                </div>
                <div className="ct-name">
                  {toast.destination} — {toast.occupied}{toast.capacity != null ? ` / ${toast.capacity}` : ''}
                </div>
                <div className="ct-meta">
                  {toast.freeDestinations?.length
                    ? `Frei: ${toast.freeDestinations.join(', ')}`
                    : 'Derzeit ist kein Zielort frei.'}
                </div>
                <div className="ct-actions">
                  <a className="ct-btn ct-btn--approve" href="/destination-board">
                    Umsetzen
                  </a>
                </div>
              </div>
              <button className="ct-close" onClick={() => dismiss(toast.id)}>✕</button>
            </div>
          )
        }

        return (
          <div key={toast.id} className={`ct-toast${isPending ? ' ct-toast--pending' : ''}`}>
            <div className={`ct-icon${isPending ? ' ct-icon--pending' : ''}`}>
              {isPending ? '⏳' : '✓'}
            </div>
            <div className="ct-body">
              <div className={`ct-title${isPending ? ' ct-title--pending' : ''}`}>
                {isPending ? 'Genehmigung ausstehend' : 'Neuer Check-in'}
              </div>
              <div className="ct-name">
                {toast.firstName} {toast.lastName}
                {toast.company ? ` · ${toast.company}` : ''}
              </div>
              <div className="ct-meta">
                {toast.checkinType === 'on-site' ? 'Vor Ort' : 'Pre-Checkin'}
                {admin?.role === 'owner' && toast.tenantSlug ? ` · ${toast.tenantSlug}` : ''}
              </div>
              {isPending && (
                <div className="ct-actions">
                  <button className="ct-btn ct-btn--approve" onClick={() => handleApprove(toast)}>
                    ✓ Genehmigen
                  </button>
                  <button className="ct-btn ct-btn--reject" onClick={() => handleReject(toast)}>
                    ✕ Ablehnen
                  </button>
                </div>
              )}
            </div>
            <button className="ct-close" onClick={() => dismiss(toast.id)}>✕</button>
          </div>
        )
      })}

      {/* Holidays to check — always last, so it never pushes a live alert out of view. */}
      {holidayTodos.map(entry => {
        const later = () => { snooze(entry.tenantId); setSnoozeTick(n => n + 1) }
        const href = admin?.role === 'owner'
          ? `/standby-settings?tenantId=${encodeURIComponent(entry.tenantId)}`
          : '/standby-settings'
        return (
          <div key={`holidays-${entry.tenantId}`} className="ct-toast ct-toast--todo">
            <div className="ct-icon ct-icon--todo">📅</div>
            <div className="ct-body">
              <div className="ct-title ct-title--todo">Feiertage prüfen</div>
              <div className="ct-meta ct-meta--wrap">
                {holidayReviewText(entry)}
                {admin?.role === 'owner' && entry.tenantName ? ` — ${entry.tenantName}` : ''}
              </div>
              <div className="ct-actions">
                {/* A plain link: CheckinToast is mounted outside the Router. */}
                <a className="ct-btn ct-btn--todo" href={href}>Prüfen</a>
                <button className="ct-btn ct-btn--later" onClick={later}>Morgen erinnern</button>
              </div>
            </div>
            <button className="ct-close" onClick={later} title="Bis morgen ausblenden">✕</button>
          </div>
        )
      })}
    </div>
  )
}
